import React, { useState, useEffect } from "react";
import { Button } from "../../Components/ui/button";
import { Input } from "../../Components/ui/input";
import { Badge } from "../../Components/ui/badge";
import {
  CalendarIcon,
  FileIcon,
  CaretSortIcon,
} from "@radix-ui/react-icons";
import { format } from "date-fns";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../Components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/ui/table";
import { Calendar } from "../../Components/ui/calendar";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import * as XLSX from "xlsx";
import { RootState } from "@/src/State/store";
import { DateRange } from "react-day-picker";
import { useToast } from "../../Components/ui/use-toast";
import AuditLogsFilterDrop, {
  AuditFilterState,
} from "../../Components/Filter/AuditLogsFilterDropdown";

interface ActivityLog {
  audit_id: number;
  event_time: string;
  event_type: string;
  action_page: string;
  user_account_id: number;
  user_full_name: string;
  user_email: string;
  impersonator_user_account_id: number | null;
  impersonator_full_name: string | null;
  impersonator_email: string | null;
  ip_address: string;
  status: string;
  comments: string;
  before_data: string | null;
  after_data: string | null;
  campaign_id: number | null;
  campaign_name: string | null;

}

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const isSuccess = status?.toLowerCase() === "success" || status?.toLowerCase() === "succedded";
  return (
    <Badge
      style={{
        backgroundColor: isSuccess ? "#16a34a" : "#dc2626",
        color: "#fff",
      }}
    >
      {status}
    </Badge>
  );
};

const CELL_LIMIT = 32000;

const splitIntoChunks = (
  value: string | null,
  columnPrefix: string,
  columnCount: number
): Record<string, string> => {
  const chunks: Record<string, string> = {};
  const str = value || "";
  for (let i = 0; i < columnCount; i++) {
    chunks[`${columnPrefix} ${i + 1}`] = str.substring(
      i * CELL_LIMIT,
      (i + 1) * CELL_LIMIT
    );
  }
  return chunks;
};

const UserActivityLogs: React.FC = () => {
  const { toast } = useToast();

  const apiUrlAdminAcc = useSelector(
    (state: RootState) => state.authentication.adminUrl
  );
  const CurrentAID = useSelector(
    (state: RootState) => state.authentication.account_id
  );

  // Data state
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  // Filter state
  const [dateRange, setDateRange] = useState<DateRange | undefined>();
  const [userSearch, setUserSearch] = useState("");
  const [debouncedUserSearch, setDebouncedUserSearch] = useState("");
  const [eventTypeOptions, setEventTypeOptions] = useState<string[]>([]);
  const [filterState, setFilterState] = useState<AuditFilterState>({
    eventType: "",
    status: "",
    ipAddress: "",
  });
  const [appliedFilters, setAppliedFilters] = useState<AuditFilterState>({
    eventType: "",
    status: "",
    ipAddress: "",
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const totalPages = Math.ceil(totalCount / rowsPerPage);

  const hasImpersonation = logs.some(
    (log) =>
      log.impersonator_full_name &&
      log.impersonator_full_name.trim() !== ""
  );

  // Debounce user search
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedUserSearch(userSearch), 500);
    return () => clearTimeout(handler);
  }, [userSearch]);

  // Load event type options
  useEffect(() => {
    if (!apiUrlAdminAcc) return;
    const fetchEventTypes = async () => {
      try {
        const res = await axios.get(`${apiUrlAdminAcc}/GetDistinctEventTypes`);
        if (res.data.status === "Success") {
          setEventTypeOptions(res.data.eventTypes ?? []);
        }
      } catch {
        // silent
      }
    };
    fetchEventTypes();
  }, [apiUrlAdminAcc]);

  const fetchLogs = async (params: {
    page: number;
    rowsPerPage: number;
    startDate?: string;
    endDate?: string;
    eventType?: string;
    userSearch?: string;
    ipAddress?: string;
    status?: string;
  }) => {
    if (!apiUrlAdminAcc) return;
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${apiUrlAdminAcc}/GetUserActivityLogs`,
        {
          params: {
            page: params.page,
            rowsPerPage: params.rowsPerPage,
            startDate: params.startDate || undefined,
            endDate: params.endDate || undefined,
            eventType: params.eventType || undefined,
            userSearch: params.userSearch || undefined,
            ipAddress: params.ipAddress || undefined,
            status: params.status || undefined,
          },
        }
      );

      if (response.data.status === "Success") {
        setLogs(response.data.logs ?? []);
        setTotalCount(response.data.totalCount);
      } else {
        setLogs([]);
        setTotalCount(0);
      }
    } catch {
      setLogs([]);
      setTotalCount(0);
      toast({ title: "Error", description: "Failed to load activity logs." });
    } finally {
      setIsLoading(false);
      if (isFirstLoading) setIsFirstLoading(false);
    }
  };

  const buildAndFetch = (
    page: number,
    rpp: number,
    filters: AuditFilterState,
    search: string,
    range: DateRange | undefined
  ) => {
    const bothDatesSelected = range?.from && range?.to;
    fetchLogs({
      page,
      rowsPerPage: rpp,
      startDate: bothDatesSelected
        ? format(range!.from!, "yyyy-MM-dd")
        : undefined,
      endDate: bothDatesSelected
        ? format(range!.to!, "yyyy-MM-dd")
        : undefined,
      eventType: filters.eventType || undefined,
      userSearch: search || undefined,
      ipAddress: filters.ipAddress || undefined,
      status: filters.status || undefined,
    });
  };

  // Initial load
  useEffect(() => {
    if (!apiUrlAdminAcc) return;
    fetchLogs({ page: 1, rowsPerPage });
    // eslint-disable-next-line
  }, [apiUrlAdminAcc]);

  // Filter/pagination changes
  useEffect(() => {
    if (!apiUrlAdminAcc) return;
    if (isFirstLoading) return;

    const singleDate = dateRange?.from && !dateRange?.to;
    if (singleDate) return;

    buildAndFetch(
      currentPage,
      rowsPerPage,
      appliedFilters,
      debouncedUserSearch,
      dateRange
    );
    // eslint-disable-next-line
  }, [
    currentPage,
    rowsPerPage,
    dateRange?.from,
    dateRange?.to,
    appliedFilters,
    debouncedUserSearch,
  ]);

  const handleApplyFilters = (filters: AuditFilterState) => {
    setAppliedFilters(filters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setAppliedFilters({ eventType: "", status: "", ipAddress: "" });
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage > 0 && newPage <= totalPages) setCurrentPage(newPage);
  };

  const formatUTC = (isoString: string) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    const day = String(date.getUTCDate()).padStart(2, "0");
    const month = String(date.getUTCMonth() + 1).padStart(2, "0");
    const year = date.getUTCFullYear();
    const hours = String(date.getUTCHours()).padStart(2, "0");
    const minutes = String(date.getUTCMinutes()).padStart(2, "0");
    return `${day}/${month}/${year} · ${hours}:${minutes}`;
  };

  const handleExport = async () => {
    if (!apiUrlAdminAcc) return;
    setIsExporting(true);
    try {
      const bothDatesSelected = dateRange?.from && dateRange?.to;
      const response = await axios.get(
        `${apiUrlAdminAcc}/ExportUserActivityLogs`,
        {
          params: {
            startDate: bothDatesSelected
              ? format(dateRange!.from!, "yyyy-MM-dd")
              : undefined,
            endDate: bothDatesSelected
              ? format(dateRange!.to!, "yyyy-MM-dd")
              : undefined,
            eventType: appliedFilters.eventType || undefined,
            userSearch: debouncedUserSearch || undefined,
            ipAddress: appliedFilters.ipAddress || undefined,
            status: appliedFilters.status || undefined,
          },
        }
      );

      if (response.data.status === "Success") {
        if (response.data.isLimited) {
          toast({
            title: "Export Limited",
            description:
              "Results exceed 10,000 records. Only the first 10,000 have been exported. Please apply filters to narrow down your results.",
          });
        }

        const exportLogs = response.data.logs ?? [];

        if (exportLogs.length === 0) {
          toast({
            title: "No Data",
            description: "No records found to export.",
          });
          return;
        }

        try {
          // Calculate dynamic column counts
          const maxBeforeLength = Math.max(
            ...exportLogs.map((log: ActivityLog) =>
              (log.before_data || "").length
            )
          );
          const maxAfterLength = Math.max(
            ...exportLogs.map((log: ActivityLog) =>
              (log.after_data || "").length
            )
          );
          const beforeColumnCount = Math.max(
            1,
            Math.ceil(maxBeforeLength / CELL_LIMIT)
          );
          const afterColumnCount = Math.max(
            1,
            Math.ceil(maxAfterLength / CELL_LIMIT)
          );

          const exportData = exportLogs.map((log: ActivityLog) => ({
            "Event Type": log.event_type,
            "Action Page": log.action_page,
            "User Name": log.user_full_name,
            "User Email": log.user_email,
            "Campaign Id": log.campaign_id ?? "",
            "Campaign Name": log.campaign_name ?? "",
            "IP Address": log.ip_address,
            Status: log.status,
            Comments: log.comments,
            "Timestamp (UTC)": formatUTC(log.event_time),
            "Impersonated By": log.impersonator_full_name?.trim()
              ? `${log.impersonator_full_name} (${log.impersonator_email})`
              : "",
            ...splitIntoChunks(
              log.before_data,
              "Before Data",
              beforeColumnCount
            ),
            ...splitIntoChunks(
              log.after_data,
              "After Data",
              afterColumnCount
            ),
          }));

          const worksheet = XLSX.utils.json_to_sheet(exportData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "Activity Logs");
          XLSX.writeFile(workbook, "UserActivityLogs.xlsx");

          toast({
            title: "Success",
            description: `${exportData.length} records exported successfully.`,
          });
        } catch (xlsxError) {
          console.error("XLSX generation error:", xlsxError);
          toast({
            title: "Error",
            description: "Failed to generate the export file.",
          });
        }
      } else {
        toast({ title: "Error", description: "Failed to export logs." });
      }
    } catch {
      toast({ title: "Error", description: "Failed to export logs." });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      {isFirstLoading ? (
        <div className="flex flex-col items-center justify-center h-[500px]">
          <CircularProgress color="primary" />
        </div>
      ) : (
        <div className="mt-4">
          {/* Filter Row */}
          <div className="flex flex-wrap items-center justify-between gap-4 -mt-6">
            {/* Left: User Search */}
            <div className="flex flex-wrap justify-start w-full md:w-auto">
              <Input
                placeholder="Search by user name, email or ID..."
                className="w-full md:w-[300px] text-[14px] font-normal text-[#171717] !mt-0"
                value={userSearch}
                onChange={(e) => {
                  setUserSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            {/* Right: Filter + Date Range + Export */}
            <div className="flex flex-wrap items-center justify-end gap-4 w-full md:w-auto">
              {/* Filter Dropdown */}
              <AuditLogsFilterDrop
                filterState={filterState}
                setFilterState={setFilterState}
                eventTypeOptions={eventTypeOptions}
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
              />

              {/* Date Range */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="min-w-[150px] w-auto justify-start text-left font-normal !mt-0"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateRange?.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, "LLL dd, y")} -{" "}
                          {format(dateRange.to, "LLL dd, y")}
                        </>
                      ) : (
                        format(dateRange.from, "LLL dd, y")
                      )
                    ) : (
                      <span>Pick a date</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    initialFocus
                    mode="range"
                    defaultMonth={dateRange?.from ?? new Date()}
                    selected={dateRange}
                    onSelect={(range) => {
                      setDateRange(range);
                      setCurrentPage(1);
                    }}
                    numberOfMonths={1}
                  />
                </PopoverContent>
              </Popover>

              {/* Export */}
              <Button
                variant="outline"
                className="w-24 text-[#020617] !mt-0"
                onClick={handleExport}
                disabled={logs.length === 0 || isExporting}
              >
                <FileIcon
                  style={{ width: "14px", height: "14px" }}
                  className="mr-1 font-bold stroke-[2]"
                />
                {isExporting ? "Exporting..." : "Export"}
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="rounded-md border mt-8">
            <div className="max-h-[60vh] overflow-y-auto">
              <Table
                className="rounded-xl whitespace-nowrap border-gray-200"
                style={{ color: "#020202", fontSize: "15px" }}
              >
                <TableHeader>
                  <TableRow className="sticky top-0 z-10 bg-white hover:bg-white">
                    <TableHead className="pl-6 pr-3 py-3 font-medium text-left">
                      Event Type
                    </TableHead>
                    <TableHead className="py-3 px-3 font-medium text-left">
                      Action Page
                    </TableHead>
                    <TableHead className="py-3 px-3 font-medium text-left">
                      User
                    </TableHead>
                    <TableHead className="py-3 px-3 font-medium text-left">
                      IP Address
                    </TableHead>
                    <TableHead className="py-3 px-3 font-medium text-left">
                      Status
                    </TableHead>
                    <TableHead className="py-3 px-3 font-medium text-left">
                      Comments
                    </TableHead>
                    <TableHead className="py-3 px-3 font-medium text-left">
                      Timestamp (UTC)
                    </TableHead>
                    {hasImpersonation && (
                      <TableHead className="py-3 px-3 font-medium text-left">
                        Impersonated By
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={hasImpersonation ? 8 : 7}
                        className="text-center py-4 text-gray-500 font-medium"
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : logs.length > 0 ? (
                    logs.map((log) => (
                      <TableRow key={log.audit_id}>
                        <TableCell className="pl-6 pr-3 py-4 text-[14px] text-left">
                          {log.event_type}
                        </TableCell>
                        <TableCell className="py-4 px-3 text-[14px] text-left">
                          {log.action_page}
                        </TableCell>
                        <TableCell className="py-4 px-3 text-[14px] text-left">
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {log.user_full_name || "-"}
                            </span>
                            <span className="text-gray-500 text-[12px] text-left">
                              {log.user_email || "-"}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="py-4 px-3 text-[14px] text-left">
                          {log.ip_address || "-"}
                        </TableCell>
                        <TableCell className="py-4 px-3 text-[14px] text-left">
                          <StatusBadge status={log.status} />
                        </TableCell>
                        <TableCell
                          className="py-4 px-3 text-[14px] max-w-[300px] truncate text-left"
                          title={log.comments}
                        >
                          {log.comments || "-"}
                        </TableCell>
                        <TableCell className="py-4 px-3 text-[14px] text-left">
                          {formatUTC(log.event_time)}
                        </TableCell>
                        {hasImpersonation && (
                          <TableCell className="py-4 px-3 text-[14px] text-left">
                            {log.impersonator_full_name &&
                            log.impersonator_full_name.trim() !== "" ? (
                              <div className="flex flex-col">
                                <span className="font-medium text-orange-600">
                                  {log.impersonator_full_name}
                                </span>
                                <span className="text-gray-500 text-[12px]">
                                  {log.impersonator_email}
                                </span>
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={hasImpersonation ? 8 : 7}
                        className="text-center py-4 text-gray-500 font-medium"
                      >
                        No activity logs found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex justify-between items-center mt-4">
            <div className="flex items-center space-x-2 text-gray-500 text-sm">
              <span>{`${
                totalCount === 0
                  ? 0
                  : (currentPage - 1) * rowsPerPage + 1
              }-${Math.min(
                currentPage * rowsPerPage,
                totalCount
              )} of ${totalCount} row(s)`}</span>
            </div>
            <div className="flex items-center space-x-4 font-medium text-sm">
              <span className="text-[#020617] font-medium text-[14px]">
                Rows per page
              </span>
              <div className="relative inline-block ml-2">
                <select
                  disabled={logs.length === 0}
                  className={`cursor-pointer border border-gray-300 rounded-md px-2 py-1 appearance-none focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                    logs.length === 0 ? "cursor-not-allowed bg-gray-100" : ""
                  }`}
                  style={{
                    width: "60px",
                    height: "30px",
                    fontSize: "12px",
                    fontWeight: "400",
                    borderColor: "#E5E7EB",
                    boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.1)",
                  }}
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                >
                  {[5, 10, 20].map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
                <CaretSortIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none text-gray-500 w-4 h-4" />
              </div>
              <div className="ml-4 mr-4">
                <span className="text-[#020617] text-[14px] font-medium">
                  {`Page ${currentPage} of ${
                    totalPages === 0 ? 1 : totalPages
                  }`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  disabled={currentPage === 1 || logs.length === 0}
                  className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${
                    currentPage === 1 || logs.length === 0
                      ? "cursor-not-allowed bg-gray-100"
                      : "hover:bg-gray-200"
                  }`}
                  onClick={() => handlePageChange(1)}
                >
                  «
                </button>
                <button
                  disabled={currentPage === 1 || logs.length === 0}
                  className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${
                    currentPage === 1 || logs.length === 0
                      ? "cursor-not-allowed bg-gray-100"
                      : "hover:bg-gray-200"
                  }`}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  ‹
                </button>
                <button
                  disabled={
                    currentPage === totalPages || logs.length === 0
                  }
                  className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${
                    currentPage === totalPages || logs.length === 0
                      ? "cursor-not-allowed bg-gray-100"
                      : "hover:bg-gray-200"
                  }`}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  ›
                </button>
                <button
                  disabled={
                    currentPage === totalPages || logs.length === 0
                  }
                  className={`border p-1 pr-2 pl-2 rounded text-gray-500 ${
                    currentPage === totalPages || logs.length === 0
                      ? "cursor-not-allowed bg-gray-100"
                      : "hover:bg-gray-200"
                  }`}
                  onClick={() => handlePageChange(totalPages)}
                >
                  »
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserActivityLogs;